/**
 * DocSidebarLive Component
 * 
 * Main implementation of the document sidebar for existing documents.
 * 
 * This is a container component that orchestrates all sidebar sections:
 * - Cover Media (image/video with auto-rotation)
 * - Assignments (users/groups assignment with dialog)
 * - Share (sharing UI, like/comment/follow, share dialog)
 * - Tags (tag display and management)
 * - Attachments (file attachments)
 * - History (activity timeline)
 * 
 * Section composition is controlled by DOC_SIDEBAR_LAYOUT config
 * (see docSidebar/sidebarLayout.tsx for order and visibility rules).
 * 
 * Apple-ish design: soft, rounded-md, clean, not cluttered.
 */

import { useState, Fragment } from "react";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import type { DocSidebarData, DocSidebarPublicShare } from "@/lib/erp/docs";
import {
  DOC_SIDEBAR_LAYOUT,
  DOC_SIDEBAR_SECTIONS,
  type SidebarRenderCtx,
} from "./docSidebar/sidebarLayout";

export type DocSidebarProps = {
  doctype: string;
  name: string;
  data: DocSidebarData;
  editable?: boolean;
};

export function DocSidebarLive({ doctype, name, data, editable = false }: DocSidebarProps) {
  const [isImageLightboxOpen, setIsImageLightboxOpen] = useState(false);

  // Guard public_share with safe default
  const public_share: DocSidebarPublicShare = data.public_share ?? {
    enabled: false,
    can_read: false,
    can_write: false,
    can_share: false,
    can_submit: false,
    shared_by: null,
    shared_on: null,
  };

  // Build render context once
  const ctx: SidebarRenderCtx = {
    doctype,
    name,
    editable,
  };

  // Open lightbox handler
  const openImageLightbox = () => setIsImageLightboxOpen(true);

  return (
    <div className="w-full max-w-xs">
      {/* Single unified sidebar container */}
      <Card className="rounded-2xl border-0 shadow-[0_2px_8px_rgba(0,0,0,0.04)] backdrop-blur-sm bg-white dark:bg-white overflow-hidden">
        <div className="p-4 sm:p-6 space-y-6">
          {/* Render sections from config */}
          {DOC_SIDEBAR_LAYOUT.map((section) => {
            // Check visibility predicates
            if (section.showWhen && !section.showWhen(ctx)) {
              return null;
            }
            if (section.requires && !section.requires(data)) {
              return null;
            }

            // Get renderer for this section
            const renderer = DOC_SIDEBAR_SECTIONS[section.id];
            if (!renderer) {
              if (import.meta.env.DEV) {
                console.warn(
                  `[DocSidebar] No renderer registered for section "${section.id}". ` +
                    `Add it to DOC_SIDEBAR_SECTIONS in src/features/common/components/docSidebar/sidebarLayout.tsx`
                );
              }
              return null;
            }

            // Render section
            return (
              <Fragment key={section.id}>
                {renderer({
                  ctx,
                  data,
                  publicShare: public_share,
                  openImageLightbox,
                })}
              </Fragment>
            );
          })}
        </div>
      </Card>

      {/* Image Lightbox */}
      {data.image && (
        <Dialog open={isImageLightboxOpen} onOpenChange={setIsImageLightboxOpen}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-0 bg-transparent border-0 shadow-none">
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={data.image}
                alt={name}
                className="max-w-full max-h-[95vh] w-auto h-auto object-contain rounded-sm"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
